import { format, formatDistance, parseISO } from 'date-fns';
import type {
  AnalyticsV2QueryParams,
  CostBreakdownGroupBy,
  CostCategoryKey,
  CostExportType,
  CostFeatureCategory,
  CostGranularity,
  CostHealth,
  CostSeriesGroupBy,
  CostSeriesPoint,
  CostTicketFilter,
  CostTicketTypes,
  CostUnit,
  CostWeek,
  CostWeeklyRow,
} from '@/types/analyticsV2';

/** Formatting and shaping for the AI cost section, kept pure so it can be tested without React. */

const usd4 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 });
const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** USD: 4 decimals under $1 (per-call and per-ticket costs are fractions of a cent), 2 otherwise. "—" for null. */
export const formatUsd = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (value === 0) return usd2.format(0);
  // A real but tiny cost (one embedding call) must not read as free.
  if (Math.abs(value) < 0.00005) return value > 0 ? '<$0.0001' : '>-$0.0001';
  // Below 0.99995 so 0.99996 is "$1.00", not "$1.0000".
  return Math.abs(value) < 0.99995 ? usd4.format(value) : usd2.format(value);
};

export const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : value.toLocaleString('en-US');

/** 0..1 → "97.8%". */
export const formatShare = (share: number | null | undefined): string =>
  share === null || share === undefined ? '—' : `${(share * 100).toFixed(1).replace(/\.0$/, '')}%`;

export type CostGroupBy = CostSeriesGroupBy | CostBreakdownGroupBy;

/**
 * A null key means "no such dimension": brand-less cost is account-level work; a null
 * model / feature / service is unknown. Brand keys are bu ids, shown by name when known.
 */
export const costKeyLabel = (
  groupBy: CostGroupBy,
  key: string | null,
  brandName?: (buId: string) => string | undefined,
): string => {
  if (groupBy === 'none') return 'Total';
  if (key === null || key === '') return groupBy === 'brand' ? 'Account-level' : 'Unknown';
  if (groupBy === 'brand') return brandName?.(key) ?? `Brand #${key}`;
  if (groupBy === 'area') return CATEGORY_LABEL[key as CostCategoryKey] ?? key;
  return key;
};

export interface CostChartSeries {
  /** The grouped key; undefined for the folded "Other" series. */
  key?: string | null;
  dataKey: string;
  label: string;
}

export interface CostChart {
  rows: Array<Record<string, string | number>>;
  series: CostChartSeries[];
}

export const OTHER_LABEL = 'Other';

/**
 * Pivots series points into one row per period with one column per key. Keeps the `top`
 * keys by total cost in the range and folds the rest into "Other", so the stack stays legible.
 * Missing period/key cells are 0 (no usage that period).
 */
export const buildCostChart = (
  points: CostSeriesPoint[],
  label: (key: string | null) => string,
  top = 6,
): CostChart => {
  const totals = new Map<string | null, number>();
  for (const p of points) totals.set(p.key, (totals.get(p.key) ?? 0) + p.costUsd);
  const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  // Folding a single key into "Other" would only rename it.
  const kept = ranked.length > top + 1 ? ranked.slice(0, top) : ranked;
  const hasOther = kept.length < ranked.length;

  const dataKeyOf = new Map<string | null, string>(kept.map((k, i) => [k, `s${i}`]));
  const series: CostChartSeries[] = kept.map((k, i) => ({ key: k, dataKey: `s${i}`, label: label(k) }));
  if (hasOther) series.push({ dataKey: 'other', label: OTHER_LABEL });

  const byPeriod = new Map<string, Record<string, string | number>>();
  for (const p of points) {
    let row = byPeriod.get(p.period);
    if (!row) {
      row = { period: p.period };
      for (const s of series) row[s.dataKey] = 0;
      byPeriod.set(p.period, row);
    }
    const dataKey = dataKeyOf.get(p.key) ?? 'other';
    row[dataKey] = Math.round(((row[dataKey] as number) + p.costUsd) * 1e6) / 1e6;
  }
  const rows = [...byPeriod.values()].sort((a, b) => String(a.period).localeCompare(String(b.period)));
  return { rows, series };
};

/**
 * The health line. Everything but unpriced calls is company-wide (kim-analytics computes it
 * across all accounts), and the line says so.
 */
export const costHealthParts = (health: CostHealth, now: Date = new Date()): string[] => {
  const tracked =
    health.providerSpendTrackedShare === null
      ? 'Provider reconciliation not configured'
      : `${formatShare(health.providerSpendTrackedShare)} of provider spend tracked (all accounts${
          health.reconciledThrough ? `, through ${health.reconciledThrough}` : ''
        })`;
  const updated = health.lastRollupAt
    ? `updated ${formatDistance(parseISO(health.lastRollupAt), now, { addSuffix: true })}`
    : 'not rolled up yet';
  return [
    tracked,
    `${formatUsd(health.unattributedCostUsd)} unattributed (all accounts)`,
    `${formatCount(health.unpricedEvents)} unpriced ${health.unpricedEvents === 1 ? 'call' : 'calls'}`,
    updated,
  ];
};

/** kim-analytics refuses `type=calls` over more than this many (inclusive) days. */
export const MAX_CALLS_EXPORT_DAYS = 31;

/** Inclusive UTC days between two YYYY-MM-DD dates. */
export const rangeDays = (fromDate: string, toDate: string): number =>
  Math.round((Date.parse(`${toDate}T00:00:00Z`) - Date.parse(`${fromDate}T00:00:00Z`)) / 86_400_000) + 1;

/** Same pattern kim-analytics sends, for when Content-Disposition is not readable. */
export const costExportFilename = (type: CostExportType, p: Pick<AnalyticsV2QueryParams, 'accountId' | 'buId' | 'fromDate' | 'toDate'>): string =>
  `ai-cost-${type}-account${p.accountId}${p.buId ? `-bu${p.buId}` : ''}-${p.fromDate}_${p.toDate}.csv`;

/** `attachment; filename="x.csv"` (or RFC 5987 `filename*=UTF-8''x.csv`) → `x.csv`; null when absent. */
export const filenameFromContentDisposition = (header: string | null | undefined): string | null => {
  if (!header) return null;
  let name = '';
  const extended = /filename\*\s*=\s*utf-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      name = decodeURIComponent(extended[1].trim());
    } catch {
      // Malformed percent-encoding: use the plain parameter.
    }
  }
  if (!name) {
    const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header);
    name = (plain?.[1] ?? plain?.[2] ?? '').trim();
  }
  // A header never picks a directory.
  return name ? name.replace(/[/\\]/g, '_') : null;
};

/** Chart grouping from the range length (inclusive days): ≤ 62 by day, ≤ 731 by month, else by year. */
export const granularityForRange = (fromDate: string, toDate: string): CostGranularity => {
  const days = rangeDays(fromDate, toDate);
  if (days <= 62) return 'day';
  return days <= 731 ? 'month' : 'year';
};

export const GRANULARITY_LABEL: Record<CostGranularity, string> = { day: 'Daily', month: 'Monthly', year: 'Yearly' };


const ratio = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/** Plain ratio (e.g. responses per ticket), up to 2 decimals. "—" for null. */
export const formatRatio = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : ratio.format(value);

const UNIT_NAMES: Record<CostUnit, { one: string; many: string }> = {
  response: { one: 'response', many: 'responses' },
  run: { one: 'SOP run', many: 'SOP runs' },
  insight: { one: 'insight', many: 'insights' },
  ticket: { one: 'ticket', many: 'tickets' },
};

/** "$0.0293 per response · 42 responses"; empty when the area has no unit. */
export const formatUnitCost = (c: Pick<CostFeatureCategory, 'unit' | 'units' | 'avgCostPerUnit'>): string => {
  if (!c.unit) return '';
  const name = UNIT_NAMES[c.unit];
  return `${formatUsd(c.avgCostPerUnit)} per ${name.one} · ${formatCount(c.units)} ${c.units === 1 ? name.one : name.many}`;
};

/** Fallback labels for the weekly report's category costs (the features endpoint sends its own). */
export const CATEGORY_LABEL: Record<CostCategoryKey, string> = {
  response_generation: 'Response generation',
  tickets_to_sop: 'Tickets to SOP',
  insights: 'Insights',
  intent_classification: 'Intent classification',
  knowledge_base: 'Knowledge base',
  testing: 'Testing',
  gap_report: 'Gap report',
  helpdesk_api: 'Helpdesk API',
  other: 'Other',
};

/** Fixed colour per feature area, so an area keeps its colour whatever the ranking. */
export const AREA_COLORS: Record<CostCategoryKey, string> = {
  response_generation: '#7C3AED',
  tickets_to_sop: '#2563EB',
  insights: '#0D9488',
  intent_classification: '#D97706',
  knowledge_base: '#DB2777',
  testing: '#65A30D',
  gap_report: '#DC2626',
  helpdesk_api: '#0891B2',
  other: '#9CA3AF',
};

/** The filter with any option the new ticket-types list no longer has reset to All; the same object when nothing changed. */
export const pruneCostFilter = (filter: CostTicketFilter, types: CostTicketTypes): CostTicketFilter => {
  let next = filter;
  if (filter.channel && !types.channels.some((c) => c.key === filter.channel)) next = { ...next, channel: undefined };
  if (filter.category) {
    const category = types.categories.find((c) => c.key === filter.category);
    if (!category || (filter.intent && !category.intents.some((i) => i.key === filter.intent)))
      next = { ...next, category: undefined, intent: undefined };
  }
  return next;
};

/** "order_status" → "Order status"; ticket-type keys arrive raw from the classifier. */
export const humanizeKey = (key: string): string => {
  const text = key.replace(/[_-]+/g, ' ').trim();
  return text ? text[0].toUpperCase() + text.slice(1) : key;
};

/** "Email · 1,204": an option name with its ticket count. */
export const withCount = (label: string, tickets: number): string => `${label} · ${formatCount(tickets)}`;

/** "Week 42 · 12–18 Oct 2026"; "Week 5 · 26 Jan – 1 Feb 2026" across months; one day when start = end. */
export const weekLabel = (w: Pick<CostWeek, 'week' | 'weekStart' | 'weekEnd'>): string => {
  const start = parseISO(w.weekStart);
  const end = parseISO(w.weekEnd);
  let range: string;
  if (w.weekStart === w.weekEnd) range = format(end, 'd MMM yyyy');
  else if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear())
    range = `${format(start, 'd')}–${format(end, 'd MMM yyyy')}`;
  else if (start.getFullYear() === end.getFullYear()) range = `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
  else range = `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`;
  return `Week ${w.week} · ${range}`;
};

/** The numbers a weekly row and a brand subtotal share. */
export type CostWeeklyTotals = Pick<
  CostWeeklyRow,
  | 'tickets'
  | 'responses'
  | 'responsesPerTicket'
  | 'ticketCostUsd'
  | 'avgTicketCostUsd'
  | 'avgResponseCostUsd'
  | 'totalCostUsd'
  | 'categories'
>;

export interface CostWeeklyGroup {
  buId: number | null;
  brandName: string;
  rows: CostWeeklyRow[];
  subtotal: CostWeeklyTotals;
}

const round6 = (n: number) => Math.round(n * 1e6) / 1e6;
const per = (n: number, d: number) => (d === 0 ? null : n / d);

/**
 * Groups the weekly rows by brand, in the order brands first appear, with a subtotal per
 * brand. Averages are recomputed from the sums (ticket cost / tickets, ticket cost /
 * responses, responses / tickets), never averaged.
 */
export const groupWeeklyRows = (rows: CostWeeklyRow[]): CostWeeklyGroup[] => {
  const groups = new Map<number | null, CostWeeklyGroup>();
  for (const row of rows) {
    let g = groups.get(row.buId);
    if (!g) {
      g = { buId: row.buId, brandName: row.brandName, rows: [], subtotal: { tickets: 0, responses: 0, responsesPerTicket: null, ticketCostUsd: 0, avgTicketCostUsd: null, avgResponseCostUsd: null, totalCostUsd: 0, categories: {} } };
      groups.set(row.buId, g);
    }
    g.rows.push(row);
    const t = g.subtotal;
    t.tickets += row.tickets;
    t.responses += row.responses;
    t.ticketCostUsd = round6(t.ticketCostUsd + row.ticketCostUsd);
    t.totalCostUsd = round6(t.totalCostUsd + row.totalCostUsd);
    for (const [k, v] of Object.entries(row.categories) as [CostCategoryKey, number][])
      t.categories[k] = round6((t.categories[k] ?? 0) + v);
  }
  for (const { subtotal: t } of groups.values()) {
    t.responsesPerTicket = per(t.responses, t.tickets);
    t.avgTicketCostUsd = per(t.ticketCostUsd, t.tickets);
    t.avgResponseCostUsd = per(t.ticketCostUsd, t.responses);
  }
  return [...groups.values()];
};
